import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";

import type { PolymorphicRef } from "types";

const KIND_CLASSES = {
  blue: "text-white bg-blue-shade-20 hover:bg-blue hover:text-white active:bg-blue-shade-40",
  "dark-gray": "text-white bg-black-tint-20 hover:bg-black-tint-30",
  gray: "text-black-tint-20 bg-black-tint-97 hover:text-black-tint-20 group-data-[dark]/themable:bg-black-tint-20 group-data-[dark]/themable:text-white",
  green: "text-white bg-green-shade-20 hover:bg-green active:bg-green-shade-40",
  red: "text-red-shade-20 bg-red-tint-95 border border-transparent hover:bg-red-tint-93 active:border-red-tint-40",
  white:
    "border border-black-tint-90 bg-white text-black-tint-40 hover:bg-black-tint-90 hover:text-black-tint-20 active:bg-black-tint-97",

  // These are used in combination with the `PillBanner` component
  "banner-black": "text-black-tint-20 bg-white border border-green-shade-40",
  "banner-green": "text-black-tint-20 bg-white border border-green-shade-40",
  "banner-light-green": "text-black-tint-20 bg-white border border-green-tint-20",
  "banner-orange": "text-black-tint-20 bg-white border border-orange-shade-20",
  "banner-light-gray": "text-black-tint-20 bg-white border border-black-tint-40",
  "banner-dark-gray": "text-black-tint-20 bg-white border border-black-tint-40",
  "banner-blue": "text-white bg-blue-shade-40 border border-blue hover:bg-blue-shade-40",
};

const SIZE_CLASSES = {
  s: "px-3 py-1 h-[2.125rem]",
  m: "px-4 py-3 h-11",
};

export type PillButtonProps<T extends React.ElementType = "button"> = BaseButtonProps<T> & {
  kind?: keyof typeof KIND_CLASSES;
  size?: keyof typeof SIZE_CLASSES;
  isMobileFullWidth?: boolean;
};

/**
 * `PillButton` is a button that is shaped like a pill, with rounded corners.
 * It is a polymorphic component, meaning it can be rendered as any HTML element.
 * By default, it is rendered as a <button> element, but you can change it to any other element by passing the `as` prop.
 *
 * To render a `PillButton` as a link, combine it with the `Link` component from react-router-dom, like this:
 * ```tsx
 * <PillButton as={Link} to="/somewhere">
 *   This looks like a button, but it's a link!
 * </PillButton>
 * ```
 */
export const PillButton = forwardRef(function PillButton<T extends React.ElementType = "button">(
  { kind, size, isMobileFullWidth, className, ...rest }: PillButtonProps<T>,
  ref: PolymorphicRef<T>,
) {
  return (
    // @ts-expect-error TypeScript doesn't understand that `BaseButton` is polymorphic
    <BaseButton
      className={twMerge(
        "flex w-fit cursor-pointer items-center justify-center whitespace-nowrap rounded-3xl text-sm font-medium disabled:cursor-not-allowed disabled:bg-black-tint-93 disabled:text-black-tint-70 disabled:group-data-[dark]/themable:text-black-tint-40",
        kind ? KIND_CLASSES[kind] : "",
        size ? SIZE_CLASSES[size] : "",
        isMobileFullWidth ? "w-full max-w-full grow md:w-auto md:max-w-fit md:grow-0" : "",
        className,
      )}
      ref={ref}
      {...rest}
    />
  );
}) as <T extends React.ElementType = "button">(
  props: PillButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
